/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import can from 'can/util/util';
import AppState from 'can/map/app/app';
import 'can/util/view_model/view_model';

const ignoreAttributesRegExp = /^(dataViewId|class|id|type|src)$/i;
const typeMatch = /\s*text\/(mustache|stache|ejs)\s*/;

let deferred = new can.Deferred();

function isIn(element, type) {
    while (element.parentNode) {
        element = element.parentNode;
        if (element.nodeName.toLowerCase() === type.toLowerCase()) {
            return true;
        }
    }

    return false;
}

function setAttr(el, attr, scope) {
    var camelized = can.camelize(attr);

    if (!ignoreAttributesRegExp.test(camelized)) {
        scope.attr(camelized, el.getAttribute(attr));
    }
}

function insertAfter(ref, element) {
    if (ref.nextSibling){
        can.insertBefore(ref.parentNode, element, ref.nextSibling);
    } else {
        can.appendChild(ref.parentNode, element);
    }
}

function render(renderer, scope, el) {
    var frag = renderer(scope);
    if (isIn(el, 'head')) {
        can.appendChild(document.body, frag);
    } else if (el.nodeName.toLowerCase() === 'script') {
        insertAfter(el, frag);
    } else {
        insertAfter(el, frag);
        el.parentNode.removeChild(el);
    }
}

function setupScope(el) {
    el = can.$(el);

    var scope = can.data(el, 'scope') || can.data(el, 'viewModel') ?
        can.viewModel(el) : new AppState();

    can.each(el.attributes || [], function (attr) {
        setAttr(el, attr.name, scope);
    });

    can.bind.call(el, 'attributes', function (ev) {
        setAttr(el, ev.attributeName, scope);
    });

    return scope;
}

function autoload() {
    var promises = [];

    can.each(can.$('[can-autorender]'), function (el) {
        if (document.readyState === 'complete' && el.hasAttribute('data-on-load')) {
            return;
        }

        if (el.style.display === 'none') {
            return;
        }
        el.style.display = 'none';

        var text = el.innerHTML || el.text,
            typeAttr = el.getAttribute('type'),
            typeInfo = typeAttr.match( typeMatch ),
            type = typeInfo && typeInfo[1],
            typeModule = 'can/view/' + type;

        if (window.System || !(window.define && window.define.amd)) {
            typeModule += '/' + type;
        }

        promises.push( can['import'](typeModule).then(function (engine) {

            engine = can[type] || engine;
            if (engine.async) {
                return engine.async(text).then(function (renderer) {
                    render(renderer, setupScope(el), el);
                });
            } else {
                var renderer = engine(text);
                return render(renderer, setupScope(el), el);
            }

        }) );
    });

    can.when.apply(can, promises).then(
        can.proxy(deferred.resolve, deferred),
        can.proxy(deferred.reject, deferred)
    );
}

if (['complete'].indexOf(document.readyState) > -1) {
    autoload();
} else {
    can.bind.call(window, 'load', autoload);
}

let promise = deferred.promise();

can.autorender = function (success, error) {
    return promise.then(success, error);
};

can.autorender.refresh = function () {
    autoload();
};

export default can.autorender;
